import { ActionModal, FormLayout } from '@kcftech/design-system';
import {
    IManufacturerResponse,
    ManufacturerCreateRequest,
    PhysicalSensorModelCreateRequest,
    SensorStandard,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { RadioButton } from 'components/forms/RadioButton';
import { RadioButtonItem } from 'components/forms/RadioButtonGroup';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { ManufacturerAutoComplete } from 'components/node_settings/ManufacturerAutoComplete';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import {
    manufacturerRules,
    modelRules,
    NotesMaxLength,
    notesRules,
} from 'pages/Settings/ThirdPartySensorModels/shared';
import {
    StyledTextArea,
    StyledTextInput,
} from 'pages/Settings/ThirdPartySensorModels/styled';
import { Controller, useForm } from 'react-hook-form';
import { AutoCompleteValue } from 'shared/autocomplete/AutoComplete';
import styled from 'styled-components';
import { Filter } from 'utils/query_v3';

const StandardTypeWrapper = styled.div`
    display: flex;
    gap: 1.6rem;
`;

const ThirdPartySensorTypeOptions: RadioButtonItem<SensorStandard>[] = [
    {
        value: SensorStandard.Analog,
        label: 'Analog',
    },
    {
        value: SensorStandard.Iepe,
        label: 'IEPE',
    },
];

const validateModel = async (value: string, manufacturerId: string | undefined) => {
    // If there's not a manufacturer id, the model is under a new manufacturer and is necessarily unique.
    if (!manufacturerId) {
        return undefined;
    }

    if (!value.trim()) {
        return 'Please enter a model number.';
    }

    const existing = await sdClient.getPhysicalSensorModels({
        filter: Filter.and(
            Filter.caseInsensitiveEq('ModelNumber', value),
            Filter.eq('ManufacturerId', manufacturerId)
        ),
    });

    if (existing.Total > 0) {
        return `Physical Sensor Model with model number "${value}" already exists. Please enter a unique model number.`;
    }
};

export type CreateThirdPartySensorForm = Readonly<{
    manufacturer: AutoCompleteValue<IManufacturerResponse>;
    modelNumber: string;
    standard: SensorStandard;
    notes: string;
}>;

export type CreateThirdPartySensorModalProps = Readonly<{
    onClose: (updated?: boolean) => void;
}>;

export function CreateThirdPartySensorModal({
    onClose,
}: CreateThirdPartySensorModalProps) {
    const { showToast } = useNotificationContext();

    const {
        control,
        watch,
        register,
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
    } = useForm<CreateThirdPartySensorForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    const manufacturer = watch('manufacturer');

    const { mutate: createThirdPartySensor, isPending: isSaving } = useMutation({
        mutationFn: async (data: CreateThirdPartySensorForm) => {
            const shouldCreateManufacturer =
                data.manufacturer !== null && typeof data.manufacturer === 'string';
            let manufacturerId = shouldCreateManufacturer
                ? undefined
                : data.manufacturer?.Id;

            if (shouldCreateManufacturer) {
                const createdManufacturer = await sdClient.createManufacturer({
                    body: new ManufacturerCreateRequest({
                        Name: data.manufacturer,
                        CorporationId: undefined,
                    }),
                });
                manufacturerId = createdManufacturer.Id;
            }

            if (!manufacturerId) {
                throw new Error(
                    'Missing manufacturer ID before creating third party sensor'
                );
            }

            await sdClient.createPhysicalSensorModel({
                body: new PhysicalSensorModelCreateRequest({
                    ManufacturerId: manufacturerId,
                    ModelNumber: data.modelNumber,
                    SensorStandard: data.standard,
                    Notes: data.notes,
                    CorporationId: undefined,
                }),
            });
        },

        onSuccess: () => {
            showToast({
                type: 'success',
                content: 'Successfully created third party sensor model',
            });
            onClose(true);
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to create third party sensor model',
            }),
    });

    const onSave = (data: CreateThirdPartySensorForm) => {
        createThirdPartySensor(data);
    };

    return (
        <ActionModal
            height={575}
            title="Create Third-Party Sensor"
            actionButtonText="Create"
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting) && !isSaving
            }
            onAction={handleSubmit(onSave)}
            onClose={onClose}
        >
            <FormLayout>
                <div className="f-14">Required *</div>
                <ValidatableLabel
                    label="Manufacturer"
                    required
                    error={errors.manufacturer}
                >
                    <Controller
                        name="manufacturer"
                        control={control}
                        rules={manufacturerRules}
                        render={({ field: { value, onChange } }) => (
                            <ManufacturerAutoComplete
                                // At time of writing, we're necessarily in global scope
                                corporationId={undefined}
                                value={value}
                                onChange={onChange}
                            />
                        )}
                    />
                </ValidatableLabel>
                <ValidatableLabel
                    label="Model Number"
                    required
                    error={errors.modelNumber}
                >
                    <StyledTextInput
                        placeholder="Enter a model number"
                        $lightBackground
                        {...register('modelNumber', {
                            ...modelRules,
                            validate: (value) =>
                                validateModel(
                                    value,
                                    manufacturer !== null &&
                                        typeof manufacturer === 'object'
                                        ? manufacturer.Id
                                        : undefined
                                ),
                        })}
                    />
                </ValidatableLabel>
                <ValidatableLabel label="Standard" required error={errors.standard}>
                    <Controller
                        name="standard"
                        control={control}
                        rules={{ required: 'Standard is required.' }}
                        render={({ field: { onChange, value } }) => (
                            <StandardTypeWrapper>
                                {ThirdPartySensorTypeOptions.map((option) => (
                                    <RadioButton
                                        key={option.value}
                                        name="standard"
                                        label={option.label}
                                        labelClassName="f-14"
                                        checked={option.value === value}
                                        onChange={() => onChange(option.value)}
                                    />
                                ))}
                            </StandardTypeWrapper>
                        )}
                    />
                </ValidatableLabel>
                <ValidatableLabel label="Notes" error={errors.notes}>
                    <StyledTextArea
                        className="mt-5"
                        placeholder="Please provide more detail for context"
                        maxLength={NotesMaxLength}
                        showLengthIndicator
                        $lightBackground
                        {...register('notes', notesRules)}
                    />
                </ValidatableLabel>
            </FormLayout>
        </ActionModal>
    );
}
